import React, { useState, useEffect, useContext } from "react";
import {
  HomeIcon,
  ClockIcon,
  RocketLaunchIcon,
  BookOpenIcon,
  UserIcon,
  Bars3Icon,
  XMarkIcon,
  MagnifyingGlassIcon,
  ChatBubbleLeftRightIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  AcademicCapIcon,
  ChevronDownIcon,
  SparklesIcon,
  BriefcaseIcon,
  CreditCardIcon,
  CodeBracketIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";

import { useNavigate, useLocation } from "react-router-dom";
import { useTenant } from "../Context/TenantContext";
import { hasTsgAiAccess, TSG_AI_URL } from "../utils/tsgAiAccess";
import API_BASE_URL from "../Config/config";
import { UserContext } from "./UserContext";
import { useSidebar } from "../Context/SidebarContext";

import { useSubscription } from "../Context/SubscriptionContext";
import EliteSelectionModal from "../components/EliteSelectionModal";

/**
 * SidebarMenu - Learner Side
 * 
 * Premium navigation for the main platform users.
 * Unified with Royal Purple design system.
 */
const SidebarMenu = ({ variant = "desktop", onRequestClose }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const { tenant } = useTenant();
  const { setUser } = useContext(UserContext);
  const { expanded, setExpanded, toggleExpanded, setHoveredLabel } = useSidebar();

  const isMainPlatform = !tenant || 
                         tenant.domain === 'theskillgenie.com' || 
                         tenant.tenantKey === 'skillgenie' ||
                         window.location.hostname === 'theskillgenie.com' ||
                         window.location.hostname === 'www.theskillgenie.com' ||
                         window.location.hostname === 'localhost';

  const isMobile = variant === "mobile";

  const [openDropdown, setOpenDropdown] = useState("");
  const [userRole, setUserRole] = useState(null);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const { subscription, eliteSelection, refreshSubscription } = useSubscription();
  const [showEliteModal, setShowEliteModal] = useState(false);


  useEffect(() => {
    if (isMobile) {
      setExpanded(true);
    }
  }, [isMobile]);

  useEffect(() => {
    if (location.pathname.startsWith('/bootcamp/')) {
      setOpenDropdown('BootCamp');
    }
  }, [location.pathname]);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user?.role) setUserRole(user.role);
  }, []);

  // Show sidebar on dashboard and all admin routes managed by AdminLayout
  const isAdminRoute =
    location.pathname === '/dashboard' ||
    location.pathname.startsWith('/superadmin/crm') ||
    location.pathname.startsWith('/superadmin/finance');
  if (!isAdminRoute && variant !== 'mobile') return null;

  const hasTsgAi = subscription.isPaid && (subscription.planDurationMonths >= 3 || subscription.plan === 'college');

  const toggleDropdown = (label) => {
    setOpenDropdown((prev) => (prev === label ? "" : label));
  };

  const confirmLogout = () => {
    localStorage.clear();
    setUser(null);
    setShowLogoutModal(false);
    navigate("/");
  };

  const menuItems = (userRole === 'admin') ? [
    {
      label: "Admin Console",
      icon: <ShieldCheckIcon className="h-5 w-5 text-orange-400" />,
      subItems: [
        { label: "Admin Users",        path: "/adminusersdashboard" },
        { label: "Admin Colleges",     path: "/admincollegesdashboard" },
        { label: "Admin Tech Grid",    path: "/admintechgrid" },
        { label: "Elite Technologies", path: "/superadmin/elite-technologies" },
        { label: "Bootcamp Admin",     path: "/adminroles" },
        { label: "Lead Ambassadors",   path: "/admin-console/lead-ambassadors" },
        { label: "Support Tickets",    path: "/supporttickets" },
        { label: "NextGen Builder",    path: "/nextgen/projects" },
        // Finance
        { label: "Finance: Enterprise", path: "/superadmin/finance/enterprise-payments" },
        // TSG AI Bot (Tee Gee) leads + conversations
        { label: "Tee Gee Leads",     path: "/superadmin/tee-gee-leads" },
        // Outreach CRM
        { label: "─── Outreach CRM ───", path: null },
        { label: "CRM Dashboard",    path: "/superadmin/crm" },
        { label: "Contacts",         path: "/superadmin/crm/contacts" },
        { label: "Bulk Upload",      path: "/superadmin/crm/bulk-upload" },
        { label: "SMS",              path: "/superadmin/crm/sms" },
        { label: "WhatsApp",         path: "/superadmin/crm/whatsapp" },
        { label: "Voice Calling",    path: "/superadmin/crm/voice" },
        { label: "Schedule Demo",    path: "/superadmin/crm/demos" },
        { label: "Demo Calendar",    path: "/superadmin/crm/calendar" },
        { label: "Comm. History",    path: "/superadmin/crm/history" },
        { label: "Analytics",        path: "/superadmin/crm/analytics" },
        { label: "CRM Settings",     path: "/superadmin/crm/settings" },
      ],
    },
    {
      label: "Profile",
      icon: <UserIcon className="h-5 w-5" />,
      subItems: [
        { label: "View Profile", path: "/profile" },
        { label: "Edit Profile", path: "/edit-profile" },
      ],
    },
  ] : [
    {
      label: "Dashboard",
      icon: <HomeIcon className="h-5 w-5" />,
      onClick: () => navigate("/dashboard"),
    },
    {
      label: "Profile",
      icon: <UserIcon className="h-5 w-5" />,
      subItems: [
        { label: "View Profile", path: "/profile" },
        { label: "Edit Profile", path: "/edit-profile" },
      ],
    },
    {
      label: "Compiler",
      icon: <CodeBracketIcon className="h-5 w-5" />,
      onClick: () => navigate("/compiler"),
    },
    {
      label: "AI Tutor (@TSG)",
      icon: <SparklesIcon className="h-5 w-5" />,
      subItems: [
        { label: "AI Tutor", path: "/tsg" },
        { label: "My Notes", path: "/notes" },
        { label: "Saved Notes", path: "/tsgmynotes" }
      ],
    },
    {
      label: "Tech Explorer",
      icon: <BookOpenIcon className="h-5 w-5" />,
      subItems: [
        { label: "Technology MCQs", path: "/techgrid" },
        { label: "Tech Documents", path: "/techmcq" },
      ],
    },
    {
      label: "Launchpad",
      icon: <RocketLaunchIcon className="h-5 w-5" />,
      subItems: [
        { label: "Launchpad MCQs", path: "/launchpadpage" },
      ],
    },
    {
      label: "Elite Mastery",
      icon: <SparklesIcon className="h-5 w-5 text-[#A855F7] animate-pulse" />,
      onClick: () => navigate("/elite-mastery"),
    },
    ...(hasTsgAi ? [{
      label: "Genie AI Assistant",
      icon: <SparklesIcon className="h-5 w-5 text-[#5B21B6]" />,
      onClick: () => {
        window.open(TSG_AI_URL, "_blank", "noopener,noreferrer");
      },
      isHighlighted: true
    }] : []),
    {
      label: "Mock Interview",
      icon: <BriefcaseIcon className="h-5 w-5" />,
      subItems: [
        { label: "Premium Interview", path: "/mockinterviewhome" },
      ],
    },
    {
      label: "Bootcamp",
      icon: <AcademicCapIcon className="h-5 w-5" />,
      subItems: [
        { label: "Enroll Now", path: "/bootcamp/enrollment" },
        { label: "My Bootcamps", path: "/bootcamp/my-bootcamps" },
      ],
    },
    {
      label: "Billing",
      icon: <CreditCardIcon className="h-5 w-5" />,
      onClick: () => navigate("/mockinterviewpayments_history"),
    },
    {
      label: "Support",
      icon: <ChatBubbleLeftRightIcon className="h-5 w-5" />,
      onClick: () => navigate("/support-student"),
    },
  ];

  const filteredMenuItems = menuItems.filter(item => 
    item.label.toLowerCase().includes(searchQuery.toLowerCase()) || 
    item.subItems?.some(sub => sub.label.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <>
      <div
        className={`
          ${isMobile ? "h-full w-full" : `fixed top-0 left-0 h-screen transition-all duration-300 z-30 ${expanded ? "w-64" : "w-20"}`}
          bg-[#843FEF] flex flex-col shadow-2xl text-white overflow-visible
        `}
      >
        {/* Logo Section */}
        <div 
          onMouseEnter={(e) => {
            if (!expanded) {
              const rect = e.currentTarget.getBoundingClientRect();
              setHoveredLabel({
                text: "The Skill Genie",
                top: rect.top + (rect.height / 2)
              });
            }
          }}
          onMouseLeave={() => setHoveredLabel(null)}
          className={`flex items-center gap-3 h-[72px] px-5 border-b border-white/10 relative group ${expanded ? '' : 'justify-start px-[22px]'}`}
        >
          <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-white/20 backdrop-blur-md flex items-center justify-center border border-white/30 shadow-inner">
             <SparklesIcon className="h-6 w-6 text-white animate-pulse" />
          </div>
          {expanded ? (
            <div className="flex flex-col min-w-0 animate-fade-in">
              <span className="text-base font-black text-white tracking-tight truncate">The Skill Genie</span>
              <span className="text-[12px] font-bold text-white uppercase tracking-widest truncate">AI Powered Learning</span>
            </div>
          ) : (
            null
          )}
        </div>

        {/* Search Bar */}
        {expanded && (
          <div className="px-4 py-4 animate-fade-in">
            <div className="relative group">
              <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8] group-focus-within:text-[#5B21B6] transition-colors" />
              <input 
                type="text" 
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 bg-[#F8FAFC] border border-[#E5E7EB] rounded-xl text-xs font-medium text-black outline-none focus:border-[#5B21B6] focus:ring-4 focus:ring-purple-50 transition-all"
              />
            </div>
          </div>
        )}

        {/* Navigation */}
      <nav className="flex-1 px-3 py-2 space-y-1 custom-scrollbar overflow-y-auto !overflow-x-visible" style={{ scrollbarGutter: 'stable' }}>
        {filteredMenuItems.map((item) => {
            const active = location.pathname.includes(item.label.toLowerCase().replace(/\s/g, '')) || openDropdown === item.label;
            const dropOpen = openDropdown === item.label;

            return (
              <div 
                key={item.label} 
                className="relative group/nav"
                onMouseEnter={(e) => {
                  if (!expanded) {
                    const rect = e.currentTarget.getBoundingClientRect();
                    setHoveredLabel({
                      text: item.label,
                      top: rect.top + (rect.height / 2)
                    });
                  }
                }}
                onMouseLeave={() => setHoveredLabel(null)}
              >
                <button
                  onClick={() => {
                    if (item.subItems?.length) {
                      if (expanded) toggleDropdown(item.label);
                      else { setExpanded(true); setOpenDropdown(item.label); }
                    } else if (item.onClick) {
                      item.onClick();
                      setExpanded(false);
                    }
                  }}
                  title={item.label}
                  className={`
                    flex items-center gap-3 w-full px-3 py-2.5 rounded-xl transition-all duration-200 group
                    ${active 
                      ? 'bg-white/20 text-white font-bold shadow-lg shadow-black/5' 
                      : 'text-white hover:bg-white/10 hover:text-white'
                    }
                    ${expanded ? '' : 'justify-start pl-[22px]'}
                    ${item.isHighlighted ? '!bg-white !text-[#5B21B6] !shadow-xl' : ''}
                    ${item.isRoyalPurple ? '!bg-[#843FEF] !text-white border border-white/30 shadow-lg shadow-black/20' : ''}
                  `}
                >
                  <span className={`flex-shrink-0 transition-transform duration-200 ${active ? 'scale-110' : 'group-hover/nav:scale-110'}`}>
                    {item.icon}
                  </span>
                  
                    {expanded && (
                      <>
                        <span className="flex-1 text-sm truncate text-left">{item.label}</span>
                        {item.subItems?.length > 0 && (
                          <ChevronDownIcon className={`h-3 w-3 transition-transform duration-300 ${dropOpen ? 'rotate-180' : ''}`} />
                        )}
                      </>
                    )}
                </button>

                {!expanded && (
                  null
                )}

                {expanded && dropOpen && item.subItems?.length > 0 && (
                  <div className="mt-1 ml-4 pl-4 border-l border-white/20 space-y-1 animate-slide-up">
                    {item.subItems.map((sub) => {
                      // Section separator (no path)
                      if (!sub.path) {
                        return (
                          <p key={sub.label} className="px-3 pt-2 pb-0.5 text-[10px] font-bold text-white/40 uppercase tracking-widest">
                            {sub.label.replace(/─/g, '').trim()}
                          </p>
                        );
                      }
                      const subActive = location.pathname === sub.path;
                      return (
                        <button
                          key={sub.label}
                          onClick={() => {
                            navigate(sub.path);
                            setExpanded(false);
                          }}
                          title={sub.label}
                          className={`
                            w-full text-left py-1.5 px-3 rounded-lg text-[13px] transition-all
                            ${subActive ? 'text-white font-bold bg-white/10' : 'text-white hover:text-white hover:bg-white/5'}
                          `}
                        >
                          {sub.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Collapse Toggle */}
        {!isMobile && (
          <div 
            className="px-3 py-2 border-t border-white/10 relative group"
            onMouseEnter={(e) => {
              if (!expanded) {
                const rect = e.currentTarget.getBoundingClientRect();
                setHoveredLabel({
                  text: "Expand Sidebar",
                  top: rect.top + (rect.height / 2)
                });
              }
            }}
            onMouseLeave={() => setHoveredLabel(null)}
          >
            <button 
              onClick={toggleExpanded}
              className="w-full flex items-center justify-center gap-2 p-2 rounded-xl text-white hover:text-white hover:bg-white/10 transition-all"
            >
              <Bars3Icon className={`h-5 w-5 transition-transform ${expanded ? 'rotate-180' : ''}`} />
              {expanded && <span className="text-sm font-bold">Collapse</span>}
            </button>
            {!expanded && (
              null
            )}
          </div>
        )}

        {/* Sign Out */}
        <div 
          className="p-3 border-t border-white/10 relative group"
          onMouseEnter={(e) => {
            if (!expanded) {
              const rect = e.currentTarget.getBoundingClientRect();
              setHoveredLabel({
                text: "Sign Out",
                top: rect.top + (rect.height / 2)
              });
            }
          }}
          onMouseLeave={() => setHoveredLabel(null)}
        >
          <button
            onClick={() => setShowLogoutModal(true)}
            title="Sign Out"
            className={`
              flex items-center gap-3 w-full px-3 py-2.5 rounded-xl transition-all
              text-red-100 hover:bg-red-500/20 font-bold
              ${expanded ? '' : 'justify-start px-[22px]'}
            `}
          >
            <ArrowRightOnRectangleIcon className="h-5 w-5 flex-shrink-0" />
            {expanded && <span className="text-sm text-left">Sign Out</span>}
          </button>
        </div>

        {/* theskillgenie.com link */}
        <div 
           className={`p-3 border-t border-white/10 text-center ${expanded ? "block" : "hidden"}`}
        >
          <a 
            href="https://www.theskillgenie.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-[10px] font-bold text-white/40 hover:text-white/80 transition-colors uppercase tracking-[0.2em]"
          >
            theskillgenie.com
          </a>
        </div>
      </div>

      {/* Logout Modal */}
      {showLogoutModal && (
        <div className="fixed inset-0 flex items-center justify-center z-[100] p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-3xl p-8 w-full max-w-sm shadow-2xl border border-white/20 animate-slide-up">
            <div className="w-14 h-14 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <ArrowRightOnRectangleIcon className="h-7 w-7 text-red-500" />
            </div>
            <h2 className="text-xl font-black text-[#111827] mb-2 text-center">Ready to leave?</h2>
            <p className="text-sm text-[#6B7280] mb-8 text-center leading-relaxed">Your progress is saved. See you back soon!</p>
            <div className="flex gap-3">
              <button onClick={() => setShowLogoutModal(false)} className="flex-1 py-3 rounded-xl font-bold text-sm text-[#6B7280] bg-[#F8FAFC] hover:bg-[#F1F5F9] transition-colors">Cancel</button>
              <button onClick={confirmLogout} className="flex-1 py-3 rounded-xl font-bold text-sm text-white bg-red-500 hover:bg-red-600 shadow-lg shadow-red-200 transition-all active:scale-95">Sign Out</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SidebarMenu;



